import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import FeaturedTrack from "../components/FeaturedTrack";
import { ShareMomentForm } from "../components/RecoveryMoments";
import { MilestoneMomentPrompt } from "../components/MilestoneMomentPrompt";
import { BookOpen, MessageCircle, Sparkles, Flame, Lock, Check, Bell, Play } from "lucide-react";

const Dashboard = () => {
  const { user } = useAuth();
  const [missions, setMissions] = useState([]);
  const [reframes, setReframes] = useState([]);
  const [library, setLibrary] = useState([]);
  const [streak, setStreak] = useState({ streak: 0, total_pivots: 0 });
  const [promptOn, setPromptOn] = useState(false);
  const [reminders, setReminders] = useState({ opt_in: false, cadence: "weekly", next_send_at: null });

  useEffect(() => {
    (async () => {
      const [m, r, l, s, rem] = await Promise.all([
        api.get("/me/missions").then((x) => x.data).catch(() => []),
        api.get("/me/reframes").then((x) => x.data).catch(() => []),
        api.get("/me/library").then((x) => x.data).catch(() => []),
        api.get("/me/streak").then((x) => x.data).catch(() => ({ streak: 0, total_pivots: 0 })),
        api.get("/me/reminders").then((x) => x.data).catch(() => ({ opt_in: false, cadence: "weekly" })),
      ]);
      setMissions(m);
      setReframes(r);
      setLibrary(l);
      setStreak(s);
      setReminders(rem);
    })();
  }, []);

  const updateReminders = async (opt_in, cadence) => {
    try {
      const { data } = await api.post("/me/reminders", { opt_in, cadence });
      setReminders(data);
      toast.success(opt_in ? `Reminders on — ${cadence}.` : "Reminders off.");
    } catch {
      toast.error("Could not update reminders");
    }
  };

  return (
    <div className="fade-in max-w-6xl mx-auto px-6 py-16">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.24em] text-teal-300/80 mb-4">Your Space</div>
        <h1 className="font-display text-4xl md:text-5xl">
          <span className="text-neutral-100">Hi, </span>
          <span className="gradient-text">{user?.name || user?.email?.split("@")[0]}</span>
        </h1>
      </div>

      <MilestoneMomentPrompt onActive={setPromptOn} />

      <div className={`card-surface p-6 mb-8 ${promptOn ? "hidden" : ""}`} data-testid="dash-share-moment">
        <div className="text-xs uppercase tracking-widest text-amber-300/80 mb-1">Share a recovery moment</div>
        <p className="text-sm text-neutral-400 mb-4">A few words about a moment that mattered. It might be what someone else needs to read today.</p>
        <ShareMomentForm />
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="card-surface p-6" data-testid="dash-streak-card">
          <Flame className="text-teal-400 mb-3" />
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Pivot Streak</div>
          <div className="font-display text-4xl">{streak.streak}</div>
          <div className="text-xs text-neutral-500 mt-1">{streak.total_pivots} total pivots logged</div>
        </div>
        <div className="card-surface p-6" data-testid="dash-missions-card">
          <Sparkles className="text-teal-400 mb-3" />
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Missions</div>
          <div className="font-display text-4xl">{missions.length}</div>
          <div className="text-xs text-neutral-500 mt-1">saved to your archive</div>
        </div>
        <div className="card-surface p-6" data-testid="dash-reframes-card">
          <MessageCircle className="text-teal-400 mb-3" />
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Reframes</div>
          <div className="font-display text-4xl">{reframes.length}</div>
          <div className="text-xs text-neutral-500 mt-1">messes turned to messages</div>
        </div>
      </div>

      <div className="mb-10">
        <FeaturedTrack />
      </div>

      <Tabs defaultValue="library" className="w-full">
        <TabsList className="bg-transparent border border-teal-500/20 rounded-full mb-6" data-testid="dash-tabs">
          <TabsTrigger value="library" data-testid="dash-tab-library" className="rounded-full data-[state=active]:bg-teal-500 data-[state=active]:text-black px-5">
            <BookOpen size={14} className="mr-2" /> Library
          </TabsTrigger>
          <TabsTrigger value="reframes" data-testid="dash-tab-reframes" className="rounded-full data-[state=active]:bg-teal-500 data-[state=active]:text-black px-5">
            <MessageCircle size={14} className="mr-2" /> Reframes
          </TabsTrigger>
          <TabsTrigger value="missions" data-testid="dash-tab-missions" className="rounded-full data-[state=active]:bg-teal-500 data-[state=active]:text-black px-5">
            <Sparkles size={14} className="mr-2" /> Missions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="library">
          <div className="grid md:grid-cols-2 gap-4">
            {library.map((t) => (
              <div key={t.id} className="card-surface p-6" data-testid={`library-item-${t.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-display text-xl">{t.title}</h3>
                  {t.unlocked ? (
                    <span className="text-xs text-teal-300 border border-teal-500/40 rounded-full px-3 py-1 inline-flex items-center gap-1">
                      <Check size={12} /> Unlocked
                    </span>
                  ) : (
                    <span className="text-xs text-neutral-400 border border-neutral-700 rounded-full px-3 py-1 inline-flex items-center gap-1">
                      <Lock size={12} /> Locked
                    </span>
                  )}
                </div>
                <p className="text-sm text-neutral-400 mb-4">{t.description}</p>

                {t.unlocked && t.type !== "soundtrack" && (
                  <div className="mb-4" data-testid={`library-progress-${t.id}`}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-neutral-500 uppercase tracking-widest">Progress</span>
                      <span className="text-teal-300">
                        {t.sections_read}/{t.total_sections} · {t.progress_pct}%
                      </span>
                    </div>
                    <div className="h-1 bg-teal-500/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-teal-400"
                        style={{ width: `${t.progress_pct}%`, transition: "width 400ms ease-out" }}
                      />
                    </div>
                  </div>
                )}

                {t.unlocked ? (
                  <Link
                    to={t.type === "soundtrack" ? "/soundtrack" : `/toolkit/${t.id}`}
                    className="btn-primary text-sm px-4 py-2 inline-flex items-center gap-2"
                    data-testid={`library-open-${t.id}`}
                  >
                    {t.type === "soundtrack" ? (
                      "Open playlist"
                    ) : t.sections_read > 0 && t.sections_read < t.total_sections ? (
                      <><Play size={14} /> Continue</>
                    ) : t.sections_read >= t.total_sections ? (
                      "Revisit"
                    ) : (
                      "Open"
                    )}
                  </Link>
                ) : (
                  <Link to="/studio" className="btn-ghost text-sm px-4 py-2 inline-block" data-testid={`library-unlock-${t.id}`}>
                    {t.free ? "Claim in Studio" : `Unlock for $${t.price}`}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Gentle Reminders */}
          <div className="mt-8 card-surface p-6" data-testid="reminders-card">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-teal-500/10 border border-teal-500/30">
                  <Bell className="text-teal-300" size={20} />
                </div>
                <div>
                  <h3 className="font-display text-xl mb-1">Gentle reminders</h3>
                  <p className="text-sm text-neutral-400 max-w-md">
                    An opt-in nudge to your inbox suggesting the next section, based on where you left off. No streaks, no guilt.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={reminders.cadence}
                  onChange={(e) => updateReminders(reminders.opt_in, e.target.value)}
                  disabled={!reminders.opt_in}
                  data-testid="reminders-cadence"
                  className="wwr-input rounded-full px-4 py-2 text-sm disabled:opacity-50"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every 2 weeks</option>
                </select>
                <Switch
                  checked={reminders.opt_in}
                  onCheckedChange={(v) => updateReminders(v, reminders.cadence)}
                  data-testid="reminders-toggle"
                />
              </div>
            </div>
            {reminders.opt_in && reminders.next_send_at && (
              <p className="text-xs text-neutral-500 mt-4" data-testid="reminders-next">
                Next nudge: {new Date(reminders.next_send_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="reframes">
          {reframes.length === 0 ? (
            <div className="card-surface p-10 text-center text-neutral-400">
              No reframes yet. <Link to="/studio" className="text-teal-300">Try the Reframer</Link>.
            </div>
          ) : (
            <div className="space-y-4">
              {reframes.map((r) => (
                <div key={r.id} className="card-surface p-6" data-testid={`reframe-item-${r.id}`}>
                  <div className="text-xs text-neutral-500 mb-3">{new Date(r.created_at).toLocaleString()}</div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">The mess</div>
                  <p className="text-neutral-300 text-sm mb-4">{r.mess}</p>
                  <div className="text-xs uppercase tracking-widest text-teal-300/80 mb-1">The message</div>
                  <p className="text-neutral-100 leading-relaxed">{r.message}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="missions">
          {missions.length === 0 ? (
            <div className="card-surface p-10 text-center text-neutral-400">
              No missions yet. <Link to="/studio" className="text-teal-300">Generate one</Link>.
            </div>
          ) : (
            <div className="space-y-4">
              {missions.map((m) => (
                <div key={m.id} className="card-surface p-6" data-testid={`mission-item-${m.id}`}>
                  <div className="text-xs text-neutral-500 mb-3">
                    {new Date(m.created_at).toLocaleString()} · mood: <span className="text-neutral-300">{m.mood}</span>
                  </div>
                  <p className="text-neutral-100 leading-relaxed">{m.mission}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
